import { GraphError } from '@microsoft/microsoft-graph-client';
import { TFunction } from 'i18next';
import logger from 'services/Logging/logService';
import AppError from 'utils/appError';
import { isObject } from 'utils/object';
import { safeStringify } from 'utils/stringify';

const isGraphError = (error: unknown): error is GraphError => error instanceof GraphError;

//Outcomes of graphUploadFileToFolder that are not a Graph/Sharepoint failure but still
//end the upload. They are raised as AppError codes so callers can tell them apart from
//a real error instead of having to interpret a single undefined return value.
export const graphUploadCancelledCode = 'uploadCancelled';
export const graphUploadEmptyFileCode = 'uploadEmptyFile';
export const graphUploadIncompleteCode = 'uploadIncomplete';

//A cancel is a user action, not an error: callers must detect it and exit quietly.
//It is deliberately absent from graphErrorMessages below so it can never render a message.
export const isUploadCancelledError = (error: unknown): boolean =>
  isObject(error) && error.code === graphUploadCancelledCode;

type GraphErrorCode =
  | 'itemnotfound'
  | 'resourcelocked'
  | 'unknownerror'
  | 'accessdenied'
  | 'user_cancelled'
  | 'consent_required';

const graphErrorMessages: Record<GraphErrorCode, string> = {
  itemnotfound: 'sharepoint:Errors.AccessDenied', //from Sharepoint
  resourcelocked: 'sharepoint:Errors.ResourceLocked', //from Sharepoint
  unknownerror: 'sharepoint:Errors.AccessDenied', //from Sharepoint, usually access related
  accessdenied: 'sharepoint:Errors.AccessDenied', //from Sharepoint
  user_cancelled: 'sharepoint:Errors.AccessDenied', //from authService
  consent_required: 'sharepoint:Errors.AccessDenied', //from authService
};

//the SDK parses the response body before checking the status; an empty body on a
//non-2xx surfaces as code "SyntaxError" with the real status on statusCode
const graphStatusMessages: Partial<Record<number, string>> = {
  401: 'sharepoint:Errors.AccessDenied',
  403: 'sharepoint:Errors.AccessDenied',
  404: 'sharepoint:Errors.AccessDenied',
};

export const getGraphSharepointErrorMsg = (error: unknown, t: TFunction<string[]>): string | undefined => {
  logger.debug('getGraphSharepointErrorMsg: start', error);

  const code = isObject(error) && typeof error.code === 'string' ? error.code : undefined;
  const message = isObject(error) && typeof error.message === 'string' ? error.message : undefined;

  //raised by graphUploadFileToFolder itself, so it is not part of the Graph code table
  if (code === graphUploadIncompleteCode) {
    return t('sharepoint:Errors.UploadIncomplete');
  }

  if (code === graphUploadEmptyFileCode) {
    return t('library:Picker.FileNull');
  }

  if (code) {
    const key = code.toLowerCase();
    if (key in graphErrorMessages) {
      return t(graphErrorMessages[key as GraphErrorCode]);
    }
  }

  if (message) {
    if (message.toLowerCase().includes('the resource you are attempting to access is locked')) {
      return t('sharepoint:Errors.ResourceLocked'); //from Sharepoint
    }

    // When backend Graph search fails due to missing permissions.
    // The user or administrator has not consented to use the application.
    if (message.includes('AADSTS65001')) {
      return t('sharepoint:Errors.IntegrationDisabled'); //from Entra ID
    }
  }

  // Only when the SDK could not read a code of its own: a real Graph code always wins,
  // and a non-SDK shape can never reach this branch.
  if (isGraphError(error) && (!error.code || error.code === 'SyntaxError')) {
    const statusMessage = graphStatusMessages[error.statusCode];
    if (statusMessage) {
      return t(statusMessage);
    }
  }

  logger.debug('getGraphSharepointErrorMsg: could not handle error');

  return undefined;
};

export const getSharePointErrorMsg = (error: unknown, t: TFunction<string[]>): string | undefined => {
  const controllerExceptionMessage = AppError.getControllerExceptionMsg(error, t);
  if (controllerExceptionMessage) return controllerExceptionMessage;

  const graphErrorMessage = getGraphSharepointErrorMsg(error, t);
  if (graphErrorMessage) return graphErrorMessage;

  const code = isObject(error) && typeof error.code === 'string' ? error.code : undefined;
  const message = isObject(error) && typeof error.message === 'string' ? error.message : '';
  const debug = isObject(error) && typeof error.debug === 'string' ? error.debug : '';

  if (code === '401' || code === '403') {
    return t('sharepoint:Errors.AccessDenied');
  }

  const errorText = `${message} ${debug}`.toLowerCase();

  if (
    errorText.includes('aadsts65001') ||
    errorText.includes('consent_required') ||
    errorText.includes('nopermissionsinaccesstoken') ||
    errorText.includes('current authenticated context is not valid')
  ) {
    return t('sharepoint:Errors.IntegrationDisabled');
  }

  if (
    errorText.includes('accessdenied') ||
    errorText.includes('access denied') ||
    errorText.includes('erroraccessdenied') ||
    errorText.includes('itemnotfound') ||
    errorText.includes('unauthorized') ||
    errorText.includes('forbidden')
  ) {
    return t('sharepoint:Errors.AccessDenied');
  }

  return undefined;
};

export const toAppError = (error: unknown): AppError | unknown => {
  if (isGraphError(error)) {
    const ae = new AppError(error.message || 'Graph request failed');
    ae.code = error.code ?? (error.statusCode > 0 ? error.statusCode.toString() : '');
    ae.stack = error.stack ?? '';
    ae.debug = safeStringify({
      statusCode: error.statusCode,
      code: error.code,
      requestId: error.requestId,
      body: error.body,
    });

    return ae;
  }

  //not an SDK error: hand it back so the caller's setError path treats it
  //exactly as it does today
  return error;
};

export const getGraphADErrorMsg = (error: AppError, t: TFunction<string[]> | TFunction<string>): string | undefined => {
  logger.debug('getGraphADErrorMsg', error);

  if (error.code) {
    switch (error.code) {
      case 'Authorization_RequestDenied':
        return t('translation:Errors.GuestAccount.NoPermissions');
      case 'user_cancelled':
        return ''; //empty means do not show
      case 'consent_required':
        return ''; //empty means do not show
    }
  }

  return undefined;
};
